<template>
  <div
    v-if="visible"
    class="calculadora-flotante fixed z-[500]"
    :style="calculadoraStyle"
    @keydown="handleKeyboard"
    tabindex="0"
  >
    <!-- Header de la calculadora (arrastrable) -->
    <div
      class="calculadora-header flex justify-between items-center bg-blue-600 dark:bg-blue-700 rounded-t-xl p-3 select-none cursor-move"
      @mousedown="iniciarArrastre"
      @touchstart="iniciarArrastreTouch"
    >
      <h3 class="text-white font-semibold text-sm flex items-center gap-2">
        <Icon icon="mdi:calculator" class="text-white" />
        Calculadora
      </h3>
      <div class="flex gap-2">
        <button
          @click="minimizar"
          class="text-white hover:text-gray-200 transition-colors"
          title="Minimizar"
        >
          <Icon icon="mdi:minus" class="text-xl" />
        </button>
        <button
          @click="cerrar"
          class="text-white hover:text-gray-200 transition-colors"
          title="Cerrar"
        >
          <Icon icon="mdi:close" class="text-xl" />
        </button>
      </div>
    </div>

    <!-- Contenido -->
    <div
      class="bg-white dark:bg-gray-800 rounded-b-xl shadow-2xl p-4"
      v-show="!minimizada"
    >
      <!-- Display -->
      <div class="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 mb-3 text-right">
        <div class="text-2xl font-bold dark:text-white truncate">{{ display || '0' }}</div>
        <div class="text-sm text-gray-500 dark:text-gray-400 truncate">{{ expression || '' }}</div>
      </div>

      <!-- Botones -->
      <div class="grid grid-cols-4 gap-2">
        <button @click="agregarNumero('7')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">7</button>
        <button @click="agregarNumero('8')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">8</button>
        <button @click="agregarNumero('9')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">9</button>
        <button @click="agregarOperador('/')" class="calc-btn bg-blue-500 hover:bg-blue-600 rounded-lg p-3 text-lg font-semibold text-white transition-colors">÷</button>

        <button @click="agregarNumero('4')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">4</button>
        <button @click="agregarNumero('5')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">5</button>
        <button @click="agregarNumero('6')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">6</button>
        <button @click="agregarOperador('*')" class="calc-btn bg-blue-500 hover:bg-blue-600 rounded-lg p-3 text-lg font-semibold text-white transition-colors">×</button>

        <button @click="agregarNumero('1')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">1</button>
        <button @click="agregarNumero('2')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">2</button>
        <button @click="agregarNumero('3')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">3</button>
        <button @click="agregarOperador('-')" class="calc-btn bg-blue-500 hover:bg-blue-600 rounded-lg p-3 text-lg font-semibold text-white transition-colors">−</button>

        <button @click="limpiar" class="calc-btn bg-red-500 hover:bg-red-600 rounded-lg p-3 text-lg font-semibold text-white transition-colors">C</button>
        <button @click="agregarNumero('0')" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">0</button>
        <button @click="agregarDecimal" class="calc-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-3 text-lg font-semibold dark:text-white transition-colors">.</button>
        <button @click="agregarOperador('+')" class="calc-btn bg-blue-500 hover:bg-blue-600 rounded-lg p-3 text-lg font-semibold text-white transition-colors">+</button>

        <button @click="calcular" class="calc-btn bg-green-500 hover:bg-green-600 rounded-lg p-3 text-lg font-semibold text-white transition-colors col-span-4">=</button>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";

export default {
  name: 'CalculadoraFlotante',
  components: { Icon },
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ['close'],
  data() {
    return {
      display: '',
      expression: '',
      operador: null,
      valorAnterior: '',
      nuevoNumero: true,
      minimizada: false,
      posX: 0,
      posY: 0,
      arrastrando: false,
      offsetX: 0,
      offsetY: 0,
    };
  },
  computed: {
    calculadoraStyle() {
      return {
        left: this.posX + 'px',
        top: this.posY + 'px'
      };
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.minimizada = false;
        this.$nextTick(() => {
          this.centrar();
          const el = this.$el;
          if (el && el.focus) el.focus();
        });
      } else {
        // Si se oculta en medio de un arrastre, limpiar listeners
        this.detenerArrastre();
      }
    }
  },
  mounted() {
    this.centrar();
    // ✅ Solo el resize es permanente. Los listeners de arrastre
    // (mouse/touch) se registran únicamente MIENTRAS se arrastra.
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
    this.detenerArrastre();
  },
  methods: {
    getAncho() {
      return window.innerWidth <= 480 ? 280 : 300;
    },
    centrar() {
      const width = this.getAncho();
      const height = 480;
      this.posX = Math.max(0, (window.innerWidth - width) / 2);
      this.posY = Math.max(0, (window.innerHeight - height) / 2);
    },
    cerrar() {
      this.limpiar();
      this.minimizada = false;
      this.detenerArrastre();
      this.$emit('close');
    },
    minimizar() {
      this.minimizada = !this.minimizada;
    },
    agregarNumero(num) {
      if (this.nuevoNumero) {
        this.display = num;
        this.nuevoNumero = false;
      } else {
        this.display += num;
      }
      this.expression += num;
    },
    agregarDecimal() {
      if (!this.display.includes('.')) {
        this.display += '.';
        this.expression += '.';
      }
    },
    agregarOperador(op) {
      const operadores = { '+': '+', '-': '-', '*': '×', '/': '÷' };
      if (this.display === '' && this.valorAnterior === '') return;
      this.operador = op;
      this.valorAnterior = this.display;
      this.nuevoNumero = true;
      this.expression += ` ${operadores[op] || op} `;
    },
    limpiar() {
      this.display = '';
      this.expression = '';
      this.operador = null;
      this.valorAnterior = '';
      this.nuevoNumero = true;
    },
    calcular() {
      if (this.operador && this.valorAnterior !== '') {
        const num1 = parseFloat(this.valorAnterior);
        const num2 = parseFloat(this.display);
        if (isNaN(num1) || isNaN(num2)) return;

        let resultado = 0;
        switch (this.operador) {
          case '+': resultado = num1 + num2; break;
          case '-': resultado = num1 - num2; break;
          case '*': resultado = num1 * num2; break;
          case '/':
            if (num2 === 0) {
              this.display = 'Error';
              this.expression = 'División por cero';
              return;
            }
            resultado = num1 / num2;
            break;
          default: return;
        }

        this.display = Number.isInteger(resultado) ? resultado.toString() : resultado.toFixed(2);
        this.expression += ` = ${this.display}`;
        this.operador = null;
        this.valorAnterior = '';
        this.nuevoNumero = true;
      }
    },
    handleKeyboard(event) {
      const key = event.key;
      if (/^[0-9]$/.test(key)) { event.preventDefault(); this.agregarNumero(key); return; }
      if (key === '.') { event.preventDefault(); this.agregarDecimal(); return; }
      if (['+', '-', '*', '/'].includes(key)) { event.preventDefault(); this.agregarOperador(key); return; }
      if (key === 'Enter' || key === '=') { event.preventDefault(); this.calcular(); return; }
      if (key === 'Escape') { event.preventDefault(); this.cerrar(); return; }
      if (key === 'Backspace') {
        event.preventDefault();
        this.display = this.display.slice(0, -1);
        this.expression = this.expression.slice(0, -1);
        return;
      }
      if (key === 'c' || key === 'C') { event.preventDefault(); this.limpiar(); return; }
    },

    // ==========================================================
    // ARRASTRE — los listeners globales existen SOLO durante el arrastre
    // ==========================================================
    limitarPosicion(x, y) {
      const width = this.getAncho();
      const height = this.minimizada ? 60 : 480;
      return {
        x: Math.max(0, Math.min(window.innerWidth - width, x)),
        y: Math.max(0, Math.min(window.innerHeight - height, y)),
      };
    },
    detenerArrastre() {
      this.arrastrando = false;
      document.removeEventListener('mousemove', this.onArrastre);
      document.removeEventListener('mouseup', this.finalizarArrastre);
      document.removeEventListener('touchmove', this.onArrastreTouch);
      document.removeEventListener('touchend', this.finalizarArrastreTouch);
      document.removeEventListener('touchcancel', this.finalizarArrastreTouch);
    },

    // --- Mouse ---
    iniciarArrastre(event) {
      if (event.target.closest('button')) return;
      if (!event.target.closest('.calculadora-header')) return;
      this.arrastrando = true;
      const rect = event.currentTarget.getBoundingClientRect();
      this.offsetX = event.clientX - rect.left;
      this.offsetY = event.clientY - rect.top;
      document.addEventListener('mousemove', this.onArrastre);
      document.addEventListener('mouseup', this.finalizarArrastre);
    },
    onArrastre(event) {
      if (!this.arrastrando) return;
      const { x, y } = this.limitarPosicion(event.clientX - this.offsetX, event.clientY - this.offsetY);
      this.posX = x;
      this.posY = y;
    },
    finalizarArrastre() {
      this.detenerArrastre();
    },

    // --- Touch ---
    iniciarArrastreTouch(event) {
      const touch = event.touches[0];
      if (!touch) return;
      if (event.target.closest('button')) return;
      if (!event.target.closest('.calculadora-header')) return;
      this.arrastrando = true;
      const rect = event.currentTarget.getBoundingClientRect();
      this.offsetX = touch.clientX - rect.left;
      this.offsetY = touch.clientY - rect.top;
      // passive:false solo mientras dura el arrastre
      document.addEventListener('touchmove', this.onArrastreTouch, { passive: false });
      document.addEventListener('touchend', this.finalizarArrastreTouch);
      document.addEventListener('touchcancel', this.finalizarArrastreTouch);
    },
    onArrastreTouch(event) {
      // ✅ Primero comprobar que realmente se está arrastrando
      if (!this.arrastrando) return;
      if (event.cancelable) event.preventDefault();
      const touch = event.touches[0];
      if (!touch) return;
      const { x, y } = this.limitarPosicion(touch.clientX - this.offsetX, touch.clientY - this.offsetY);
      this.posX = x;
      this.posY = y;
    },
    finalizarArrastreTouch() {
      this.detenerArrastre();
    },

    onWindowResize() {
      const { x, y } = this.limitarPosicion(this.posX, this.posY);
      this.posX = x;
      this.posY = y;
    }
  }
};
</script>

<style scoped>
.calculadora-flotante {
  width: 300px;
  user-select: none;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  overflow: hidden;
  background: transparent;
}
/* touch-action:none SOLO en la barra de arrastre: así el navegador
   no intenta hacer scroll mientras se arrastra la calculadora */
.calculadora-header { cursor: move; touch-action: none; }
.calc-btn { cursor: pointer; min-height: 48px; }
.calc-btn:active { transform: scale(0.95); }
@media (max-width: 480px) {
  .calculadora-flotante { width: 280px; }
}
</style>